
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormField } from "./BaseForm";
import { ThirdPartyLiabilityFormData, FormProps } from "@/types/forms";

type ThirdPartyLiabilityFormProps = FormProps<ThirdPartyLiabilityFormData>;

const ThirdPartyLiabilityForm = ({ onSubmit, initialData = {}, buttonText = "Continue" }: ThirdPartyLiabilityFormProps) => {
  const [formData, setFormData] = useState<ThirdPartyLiabilityFormData>({
    businessActivity: initialData.businessActivity || "",
    customersVisit: initialData.customersVisit || "",
    numberOfEmployees: initialData.numberOfEmployees || "",
    annualTurnover: initialData.annualTurnover || "",
    operationLocations: initialData.operationLocations || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.businessActivity) newErrors.businessActivity = "Business activity is required";
    if (!formData.customersVisit) newErrors.customersVisit = "Please specify if customers visit";
    if (!formData.numberOfEmployees) newErrors.numberOfEmployees = "Number of employees is required";
    if (!formData.annualTurnover) newErrors.annualTurnover = "Annual turnover is required";
    if (!formData.operationLocations) newErrors.operationLocations = "Operation locations is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const updateField = <K extends keyof ThirdPartyLiabilityFormData>(field: K, value: ThirdPartyLiabilityFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <FormField label="Business activity / operations" required>
        <Textarea
          placeholder="Describe your business activities and operations"
          value={formData.businessActivity}
          onChange={(e) => updateField('businessActivity', e.target.value)}
        />
        {errors.businessActivity && <p className="text-red-500 text-sm">{errors.businessActivity}</p>}
      </FormField>

      <FormField label="Do customers visit your premises?" required>
        <Select value={formData.customersVisit} onValueChange={(value) => updateField('customersVisit', value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select option" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
        {errors.customersVisit && <p className="text-red-500 text-sm">{errors.customersVisit}</p>}
      </FormField>

      <FormField label="Number of employees" required>
        <Input
          type="number"
          placeholder="Enter number of employees"
          value={formData.numberOfEmployees}
          onChange={(e) => updateField('numberOfEmployees', e.target.value)}
        />
        {errors.numberOfEmployees && <p className="text-red-500 text-sm">{errors.numberOfEmployees}</p>}
      </FormField>

      <FormField label="Annual turnover (AED)" required>
        <Input
          type="number"
          placeholder="Enter annual turnover in AED"
          value={formData.annualTurnover}
          onChange={(e) => updateField('annualTurnover', e.target.value)}
        />
        {errors.annualTurnover && <p className="text-red-500 text-sm">{errors.annualTurnover}</p>}
      </FormField>

      <FormField label="Locations of operation" required>
        <Select value={formData.operationLocations} onValueChange={(value) => updateField('operationLocations', value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select option" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="uae_only">UAE only</SelectItem>
            <SelectItem value="international">International</SelectItem>
          </SelectContent>
        </Select>
        {errors.operationLocations && <p className="text-red-500 text-sm">{errors.operationLocations}</p>}
      </FormField>

      <Button type="submit" className="w-full">
        {buttonText}
      </Button>
    </form>
  );
};

export default ThirdPartyLiabilityForm;
